import { useState, useEffect } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { projectsApi, ApiComponent } from '../../utils/api'
import { useEditorStore } from '../../store/editor.store'

function ComponentItem({ component, onDelete }: { component: ApiComponent; onDelete: () => void }) {
  const blockType = component.templateData?.blockType ?? ''
  const blockData = (component.templateData?.blockData ?? {}) as Record<string, unknown>

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `component-${component.id}`,
    data: { type: 'new-block', blockType, savedBlockData: blockData },
  })

  function handleClick() {
    if (!blockType) return
    const { page, selectedSectionId, addSection, addBlock } = useEditorStore.getState()
    const targetSection = selectedSectionId
      ? page.sections.find(s => s.id === selectedSectionId)
      : page.sections[page.sections.length - 1]

    if (targetSection) {
      addBlock(targetSection.id, blockType, blockData)
    } else {
      addSection()
      setTimeout(() => {
        const { page: p, addBlock: ab } = useEditorStore.getState()
        const sec = p.sections[p.sections.length - 1]
        if (sec) ab(sec.id, blockType, blockData)
      }, 0)
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      style={{
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        background: 'var(--bg-2)',
        borderRadius: '8px',
        cursor: 'grab',
        border: '1px solid var(--border)',
        transition: 'border-color 0.15s',
        userSelect: 'none',
      }}
    >
      <span style={{ fontSize: '18px', flexShrink: 0 }}>★</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{component.name}</div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {component.category !== 'custom' ? `${component.category} · ` : ''}{blockType || 'block'}
        </div>
      </div>
      <button
        onClick={e => { e.stopPropagation(); if (confirm(`Удалить компонент «${component.name}»?`)) onDelete() }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)', padding: '2px 6px', flexShrink: 0, lineHeight: 1 }}
        title="Удалить"
      >×</button>
    </div>
  )
}

export function ComponentsPanel() {
  const [components, setComponents] = useState<ApiComponent[]>([])
  const [loading, setLoading] = useState(true)

  const projectId = typeof window !== 'undefined' ? localStorage.getItem('bst-project-id') : null

  const load = () => {
    if (!projectId) { setLoading(false); return }
    setLoading(true)
    projectsApi.listComponents(projectId)
      .then(setComponents)
      .catch(() => setComponents([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [projectId])

  const handleDelete = async (id: string) => {
    if (!projectId) return
    await projectsApi.deleteComponent(projectId, id).catch(() => {})
    load()
  }

  if (!projectId) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>★</div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Нет активного проекта</div>
        <div>Компоненты доступны только в проектах Project Engine</div>
      </div>
    )
  }

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Загрузка...</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
          Компоненты проекта — нажмите или перетащите на холст. Чтобы сохранить, нажмите <strong>★</strong> в тулбаре блока.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {components.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>★</div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Нет компонентов</div>
            <div>Выберите блок на холсте и нажмите «★» чтобы сохранить</div>
          </div>
        ) : (
          components.map(c => (
            <ComponentItem key={c.id} component={c} onDelete={() => handleDelete(c.id)} />
          ))
        )}
      </div>

      {components.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {components.length} компонентов
        </div>
      )}
    </div>
  )
}
